import type { FileStore } from "../../../runtime/fileStore";
import type { DataSession } from "../../../data/core/session";
import { trpc } from "../../../data/core/trpc";
import {
  OFFLINE_LIBRARY_QUERY_KEY,
  TODAYS_TOP_HITS_ID,
  DOWNLOAD_DIR,
} from "../constants";
import { getQueryKey } from "@trpc/react-query";
import { playlistQueryKey } from "../../../data/queries/collections";

export const ensureDir = async (
  dir: string,
  Directory: FileStore["Directory"],
) => {
  const directory = new Directory(dir);
  if (!directory.exists) {
    console.log(`:: DownloadManager :: Creating directory ${dir}`);
    directory.create({ intermediates: true });
  }
};

export const invalidateQueries = async (
  data: DataSession,
  collectionId: string,
  collectionType: "album" | "playlist",
  songIds?: string[],
) => {
  const { queryClient, utils: trpcUtils } = data;
  try {
    if (collectionType === "album") {
      await Promise.all([
        trpcUtils.catalog.albums.getById.invalidate([collectionId]),
        trpcUtils.catalog.songs.getByAlbumIds.invalidate([collectionId]),
      ]);
    } else if (collectionType === "playlist") {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: getQueryKey(trpc.user.library.listItems),
        }),
        songIds &&
          songIds.length > 0 &&
          trpcUtils.catalog.songs.getById.invalidate(songIds),
        trpcUtils.catalog.songs.getByPlaylistIds.invalidate([collectionId]),
        queryClient.invalidateQueries({
          queryKey: playlistQueryKey(collectionId),
        }),
      ]);
    }

    await queryClient.invalidateQueries({
      queryKey: [OFFLINE_LIBRARY_QUERY_KEY],
    });

    if (collectionId.includes(TODAYS_TOP_HITS_ID)) {
      await queryClient.invalidateQueries({
        queryKey: getQueryKey(trpc.catalog.playlists.getTodaysTopHits),
      });
    }
  } catch (error) {
    console.error(":: DownloadManager :: Error invalidating queries:", error);
  }
};

// Trailing URL segments that are generic and would collide across albums.
// Album image URLs come in shapes like:
//   .../images/{uuid}
//   .../images/{uuid}/image
//   .../images/{uuid}/image.jpg
// When the last segment is generic ("image", "image.jpg", …) we walk back to
// the UUID — otherwise every album sharing the suffix resolves to the same
// local file and one song ends up showing another song's cover art.
const GENERIC_IMAGE_SEGMENT = /^image(\.[a-z0-9]+)?$/i;

export const getOfflineImagePath = (
  remoteUrl: string,
  directory = DOWNLOAD_DIR,
) => {
  const segments = remoteUrl.split("?")[0].split("/").filter(Boolean);
  while (
    segments.length > 1 &&
    GENERIC_IMAGE_SEGMENT.test(segments[segments.length - 1])
  ) {
    segments.pop();
  }
  const imageId = segments[segments.length - 1];
  return `${directory}/images/${imageId}`;
};

export const getOfflineTrackPath = (
  trackId: string,
  directory = DOWNLOAD_DIR,
) => {
  return `${directory}/audio/${trackId}.mp4`;
};

export const isTrackReferencedByAnotherCollection = (
  trackId: string,
  collections: { trackIds: string[] | null }[],
) => collections.some((collection) => collection.trackIds?.includes(trackId));

/**
 * Reconstructs an image path using the current DOWNLOAD_DIR.
 * This handles iOS app updates that can change the app's document directory GUID
 * while keeping the files intact. The stored path in SQLite may have an old GUID,
 * so we extract just the filename and rebuild with the current DOWNLOAD_DIR.
 *
 * @param storedPath - The path stored in SQLite (may have stale GUID)
 * @returns The reconstructed path with current DOWNLOAD_DIR, or empty string if invalid
 */
export const reconstructImagePath = (
  storedPath: string | null | undefined,
  directory = DOWNLOAD_DIR,
): string => {
  if (!storedPath) return "";

  // Only reconstruct paths that are actually download paths.
  // Other local file:// URIs (e.g. image picker temps) pass through unchanged.
  if (!storedPath.includes("/downloads/")) {
    return storedPath;
  }

  // Extract just the filename from the stored path
  // Path format: file:///.../.../Documents/downloads/images/filename.ext
  // or: /var/mobile/.../Documents/downloads/images/filename.ext
  const filename = storedPath.split("/").pop();
  if (!filename) return storedPath;

  // Rebuild with current DOWNLOAD_DIR
  return `${directory}/images/${filename}`;
};
